import {EventEmitter, Injectable} from "@angular/core";
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from "@angular/common/http";
import {BehaviorSubject, catchError, Observable, throwError} from "rxjs";
import {User} from "../interface/user";
import {environment} from "../../environments/environment";
import {CookieService} from 'ngx-cookie-service';
import {UserResponse} from "../interface/userResponse";
import {UserUpdateRequest} from "../interface/userUpdateRequest";
import {Router} from "@angular/router";
import {VerifyRequest} from "../interface/verifyRequest";
import {UserRole} from "../enums/UserRole";
import {UserShortResponse} from "../interface/userShortResponse";
import {UserFilter} from '../interface/user-filter.interface';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private redirectUrlVar: string | null = null;

  private userTokenVar = 'USER_TOKEN'
  private userEmailVar = 'USER_EMAIL'
  private adminTokenVar = 'ADMIN_TOKEN'
  private adminEmailVar = 'ADMIN_EMAIL'
  private roleVar = 'ROLE'

  private apiUrl = environment.apiUrl;
  private userSource = new BehaviorSubject<UserResponse | any>(null);

  currentUser = this.userSource.asObservable();
  updatedProfileImageUrl: EventEmitter<string | null> = new EventEmitter<string | null>();

  private currentProfileImageSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  public currentProfileImageObservable: Observable<string | null> = this.currentProfileImageSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router
  ) {
  }

  findAll(page: number = 0, size: number = 9): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get(`${this.apiUrl}/user/all`, {params})
  }

  filterUsers(page: number = 0, size: number = 9, filter: UserFilter): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get(`${this.apiUrl}/user/filter`, {params});
  }

  register(user: User): Observable<any> {
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return this.http.post(`${this.apiUrl}/user/register`, user, {headers})
  }

  verifyPhone(verifyRequest: VerifyRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/user/verify-phone-number`, verifyRequest);
  }

  verifyEmail(verificationCode: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/user/verify-email?verificationCode=${verificationCode}`);
  }

  checkResetPasswordLink(resetToken: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/user/check-reset-link?resetToken=${resetToken}`);
  }

  uploadUserImage(userId: bigint, formData: FormData): Observable<any> {
    return this.http.post(`${this.apiUrl}/user/upload/${userId}`, formData);
  }

  editUser(id: bigint, formData: UserUpdateRequest): Observable<any> {
    const updateUserUrl = `${this.apiUrl}/user/update/${id}`;
    return this.http.put(updateUserUrl, formData)
  }

  findByEmail(email: string): Observable<UserResponse> {
    return this.http.get<UserResponse>(`${this.apiUrl}/user/email?email=${email}`)
  }

  login(user: any): Observable<any>  {
    const url = `${this.apiUrl}/user/authenticate`;
    return this.http.post(url, user)
  }

  sendPasswordResetLink(email: string): Observable<UserResponse> {
    return this.http.post<UserResponse>(`${this.apiUrl}/user/reset-password`, { email });
  }

  resetPassword(newPassword: string, confirmPassword: string, token: string): Observable<any> {
    const url = `${this.apiUrl}/user/change-password`;
    const body = {newPassword, confirmPassword, token};
    return this.http.post(url, body).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(error.error.message || 'Failed to reset password');
      })
    );
  }

  setUser(user: UserResponse): void {
    this.userSource.next(user);
  }

  logout() {
    this.http.post(`${this.apiUrl}/logout`, {}).subscribe(response => {
        this.clearUserInformationFromCookies();
        this.userSource.next(null);
        this.router.navigate(['/home']);
    });
  }

  logoutAndNavigateToLogin() {
    this.http.post(`${this.apiUrl}/logout`, {}).subscribe(() => {
        this.clearUserInformationFromCookies();
        this.userSource.next(null);
        this.router.navigate(['/login']);
    });
  }

  logoutAdmin() {
    this.http.post(`${this.apiUrl}/logout`, {}).subscribe(response => {
        this.clearAdminInformationFromCookies();
        this.router.navigate(['/admin/login'])
    });
  }

  clearDates(redirectUrl?: string) {
    this.clearUserInformationFromCookies();
    this.clearAdminInformationFromCookies();
    this.userSource.next(null);

    if (redirectUrl) {
      this.router.navigate([redirectUrl]);
    } else {
      this.router.navigate(['/home']);
    }
  }

  clearAdminRole() {
    this.cookieService.delete(this.roleVar, "/admin");
  }

  getProfileImage(userId: bigint): Observable<any> {
    return this.http.get(`${this.apiUrl}/user/profile-image/${userId}`, { responseType: 'blob'});
  }

  getImage(fileName: string){
    return `${this.apiUrl}/user/getImage/${fileName}`;
  }

  deleteById(userId: bigint): Observable<void> {
    const url = `${this.apiUrl}/user`;
    const body = {
      id: userId
    }
    return this.http.delete<void>(url, {body: body});
  }

  public setCurrentProfileImageUrl(url: string | null): void {
    this.currentProfileImageSubject.next(url);
  }

  public getCurrentProfileImageUrl(): Observable<string | null> {
    return this.currentProfileImageObservable;
  }

  get email(){
    return this.cookieService.get(this.userEmailVar);
  }

  get adminEmail(){
    return this.cookieService.get(this.adminEmailVar);
  }

  get role(){
    return this.cookieService.get(this.roleVar);
  }

  get adminToken() {
    return this.cookieService.get(this.adminTokenVar);
  }

  get token() {
    return this.cookieService.get(this.userTokenVar);
  }

  resendVerificationCode(userPhoneNumber: string, countryISO: string): Observable<any> {
    const body = {userPhoneNumber, countryISO};
    return this.http.post(`${this.apiUrl}/user/resend-verification-code`, body, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  getUserAttemptsCount(userPhoneNumber: string, countryISO: string): Observable<number> {
    const body = {userPhoneNumber, countryISO};
    return this.http.post<number>(`${this.apiUrl}/user/attempts`, body, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  getUsersCount():Observable<bigint> {
    return this.http.get<bigint>(`${this.apiUrl}/user/count`);
  }

  search(searchQuery: string, page: number = 0, size: number = 9):Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('search', searchQuery);
    return this.http.get<any>(`${this.apiUrl}/user/search`, { params });
  }

  getUsersForAdmin(): Observable<UserShortResponse[]> {
    return this.http.get<UserShortResponse[]>(`${this.apiUrl}/user/all-individual`);
  }

  downloadUser(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/user/download-users`, {responseType: 'blob'});
  }

  public setAdminToken(token: string, expirationDate: Date) {
    this.setAdminCookie(this.adminTokenVar, token, expirationDate);
  }

  public setAdminEmail(email: string, expirationDate: Date) {
    this.setAdminCookie(this.adminEmailVar, email, expirationDate);
  }

  public setAdminRole(role: string, expirationDate: Date) {
    this.setAdminCookie(this.roleVar, role, expirationDate);
  }

  public setToken(token: string, expirationDate: Date) {
    this.setCookie(this.userTokenVar, token, expirationDate);
  }

  public setEmail(email: string, expirationDate: Date) {
    this.setCookie(this.userEmailVar, email, expirationDate)
  }

  public setRole(role: string, expirationDate: Date) {
    this.setCookie(this.roleVar, role, expirationDate)
  }

  private setCookie(key: string, value: any, expirationDate: Date) {
    if (value) {
      this.cookieService.delete(key);
      this.cookieService.set(key, value, expirationDate, '/', '', true, 'Strict');
    } else {
      this.cookieService.delete(key, '/');
    }
  }

  private setAdminCookie(key: string, value: any, expirationDate: Date) {
    if (value) {
      this.cookieService.delete("ROLE");
      this.cookieService.set(key, value, expirationDate, '/admin', '', true, 'Strict');
    } else {
      this.deleteAdminValueFromCookies(key)
    }
  }

  private clearAdminInformationFromCookies() {
    this.deleteAdminValueFromCookies(this.adminTokenVar)
    this.deleteAdminValueFromCookies(this.adminEmailVar)
    this.deleteAdminValueFromCookies(this.roleVar)
  }

  private clearUserInformationFromCookies() {
    this.deleteValueFromCookiesByKey(this.userTokenVar);
    this.deleteValueFromCookiesByKey(this.userEmailVar);
    this.deleteValueFromCookiesByKey(this.roleVar);
  }

  private deleteAdminValueFromCookies(key: string) {
    this.cookieService.delete(key, '/admin');
  }

  private deleteValueFromCookiesByKey(key: string) {
    this.cookieService.delete(key, "/")
  }

  isLoggedIn(): boolean {
    const token = this.cookieService.get(this.userTokenVar);
    const role = this.cookieService.get(this.roleVar);
    return !!token && role === UserRole.USER;
  }

  setRedirectUrl(url: string): void {
    this.redirectUrlVar = url;
  }

  getRedirectUrl(): string | null {
    return this.redirectUrlVar;
  }

  clearRedirectUrl(): void {
    this.redirectUrlVar = null;
  }
}
